/**
 * Avatar utility functions for generating and managing user avatars
 */

/**
 * Generate a default avatar URL using DiceBear API or similar service
 * @param {string} seed - Unique identifier (wallet address, username, etc.)
 * @param {string} style - Avatar style ('avataaars', 'identicon', 'initials', etc.)
 * @returns {string} Avatar URL
 */
export const generateDefaultAvatar = (seed, style = 'avataaars') => {
  if (!seed) return null;
  
  // Use DiceBear API for generating consistent avatars
  const cleanSeed = encodeURIComponent(seed);
  return `https://api.dicebear.com/7.x/${style}/svg?seed=${cleanSeed}&backgroundColor=1d9bf0,8b5cf6&radius=50`;
};

/**
 * Generate initials from display name or username
 * @param {string} name - Display name or username
 * @returns {string} Initials (1-2 characters)
 */
export const generateInitials = (name) => {
  if (!name) return '?';
  
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

/**
 * Get the best available avatar for a user
 * @param {Object} profile - User profile object
 * @param {string} walletAddress - User's wallet address as fallback
 * @returns {Object} Avatar data with type and source
 */
export const getBestAvatar = (profile, walletAddress) => {
  // Priority 1: User uploaded profile image (check multiple possible fields)
  const profileImageSrc = profile?.profileImage || profile?.avatarUrl || profile?.avatar || profile?.profilePicture;
  if (profileImageSrc && profileImageSrc.trim() !== '' && profileImageSrc !== 'undefined' && profileImageSrc !== 'null') {
    // Validate that it's a proper image URL
    if (profileImageSrc.startsWith('http') || profileImageSrc.startsWith('data:image') || profileImageSrc.startsWith('blob:')) {
      return {
        type: 'image',
        src: profileImageSrc,
        alt: profile?.displayName || profile?.username || 'User Profile Picture',
        isUserUploaded: true
      };
    }
  }
  
  // Priority 2: Only generate avatar if no profile image is available
  if (walletAddress) {
    return {
      type: 'generated',
      src: generateDefaultAvatar(walletAddress),
      alt: profile?.displayName || profile?.username || 'Generated Avatar',
      isUserUploaded: false
    };
  }
  
  // Priority 3: Initials
  const name = profile?.displayName || profile?.username;
  if (name) {
    return {
      type: 'initials',
      text: generateInitials(name),
      alt: name,
      isUserUploaded: false
    };
  }
  
  // Priority 4: Default icon
  return {
    type: 'icon',
    icon: '🚀',
    alt: 'User',
    isUserUploaded: false
  };
};

/**
 * Generate a gradient background color based on a string
 * @param {string} seed - String to generate color from
 * @returns {string} CSS gradient string
 */
export const generateGradientFromSeed = (seed) => {
  if (!seed) return 'linear-gradient(135deg, #1d9bf0, #8b5cf6)';
  
  // Simple hash function to generate consistent colors
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Generate hue based on hash
  const hue1 = Math.abs(hash) % 360;
  const hue2 = (hue1 + 60) % 360; // Complementary color
  
  return `linear-gradient(135deg, hsl(${hue1}, 70%, 60%), hsl(${hue2}, 70%, 60%))`;
};
